import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import Select from 'app/frontend/base/components/Select'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { useState } from 'react'
import {
  getSelectableRolesForCharacter,
  SignupRoleSelect,
  SignupStatusSelect,
  viewerSignupCharacterSelectOptions,
} from './viewerSignupShared'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: React.ReactNode
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  currentStatus: wowaudit.EventParticipantReference['status']
  currentComment?: string
  isSaving?: boolean
  onSave: (
    status: wowaudit.EventParticipantReference['status'],
    comment: string,
    characterId?: number,
    role?: CharacterRole,
  ) => void
  canChangeCharacter?: boolean
  selectableCharacters?: wowaudit.CharacterReference[]
  currentCharacterId?: string
  currentRole?: CharacterRole
  align?: 'start' | 'end' | 'center'
}

const EventParticipantEditPopover = ({
  open,
  onOpenChange,
  trigger,
  selectableStatuses,
  currentStatus,
  currentComment,
  isSaving,
  onSave,
  canChangeCharacter,
  selectableCharacters = [],
  currentCharacterId = '',
  currentRole = 'melee',
  align = 'end',
}: Props) => {
  const [draftStatus, setDraftStatus] = useState(currentStatus)
  const [draftComment, setDraftComment] = useState(currentComment ?? '')
  const [draftCharacterId, setDraftCharacterId] = useState(currentCharacterId)
  const [draftRole, setDraftRole] = useState<CharacterRole>(currentRole)

  const selectedCharacter = selectableCharacters.find(
    (c) => c.id.toString() === draftCharacterId,
  )
  const selectableRoles = getSelectableRolesForCharacter(
    selectedCharacter?.classId,
  )

  const isDirty =
    draftStatus !== currentStatus ||
    draftComment !== (currentComment ?? '') ||
    Boolean(canChangeCharacter && draftCharacterId !== currentCharacterId) ||
    Boolean(canChangeCharacter && draftRole !== currentRole)

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) return
    setDraftStatus(currentStatus)
    setDraftComment(currentComment ?? '')
    setDraftCharacterId(currentCharacterId)
    setDraftRole(currentRole)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className='p-3 flex flex-col gap-2' align={align}>
        {canChangeCharacter ? (
          <Select
            value={draftCharacterId}
            setValue={(value) => {
              if (!value) return
              setDraftCharacterId(value)
              const nextCharacter = selectableCharacters.find(
                (c) => c.id.toString() === value,
              )
              if (!nextCharacter) return
              const nextRoles = getSelectableRolesForCharacter(
                nextCharacter.classId,
              )
              if (!nextRoles.length) return
              if (nextRoles.includes(draftRole)) return
              if (
                nextCharacter.role &&
                nextRoles.includes(nextCharacter.role)
              ) {
                setDraftRole(nextCharacter.role)
                return
              }
              setDraftRole(nextRoles[0])
            }}
            options={viewerSignupCharacterSelectOptions(selectableCharacters)}
            disabled={isSaving}
            optionWidth='fit'
          />
        ) : null}
        {canChangeCharacter ? (
          <SignupRoleSelect
            value={draftRole}
            setValue={setDraftRole}
            roles={selectableRoles}
            disabled={isSaving || selectableRoles.length === 0}
          />
        ) : null}
        <SignupStatusSelect
          statuses={selectableStatuses}
          value={draftStatus}
          setValue={setDraftStatus}
          disabled={isSaving}
        />
        <Input
          value={draftComment}
          disabled={isSaving}
          onChange={(e) => setDraftComment(e.target.value)}
          placeholder='Comment'
        />
        <Button
          size='sm'
          className='w-full'
          disabled={!isDirty || isSaving}
          onClick={() => {
            onSave(
              draftStatus,
              draftComment,
              canChangeCharacter ? Number(draftCharacterId) : undefined,
              canChangeCharacter ? draftRole : undefined,
            )
            onOpenChange(false)
          }}
        >
          Save
        </Button>
      </PopoverContent>
    </Popover>
  )
}

export default EventParticipantEditPopover
